import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config/environment';
import { buildSymbolMap, currencySymbol, FALLBACK_SYMBOL_MAP, SymbolMap } from '../utils/currency';
import {
  AppointmentPaymentStatus,
  updateMeetingStatus,
  recordManualPayment,
  resetManualPayment,
  updatePricingStatus,
  setAppointmentPricing,
  updateSessionDetails,
} from '../services/paymentService';
import {
  Invoice,
  createInvoice,
  getInvoiceForSession,
  sendInvoice,
  resendInvoice,
  voidInvoice,
} from '../services/invoiceService';
import { AppointmentType, APPOINTMENT_TYPES, getAppointmentTypeLabel } from '../constants/appointmentTypes';

// ── Minimal icons (inline SVG to avoid new dependency) ────────────────────────
const XMarkIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const VideoCameraIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.369A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
  </svg>
);
const XCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ── Types ──────────────────────────────────────────────────────────────────────

interface Session {
  appointmentId?: string;
  clientName: string;
  date: string;
  time: string;
  duration: number;
  appointmentStatus?: string;
  appointmentType?: AppointmentType;
  barterNote?: string;
  googleMeetLink?: string;
  packageEnrollmentId?: string | null;
  packageName?: string | null;
  coachClientId?: number;
}

interface Currency { code: string; name: string; }

interface Props {
  session: Session;
  paymentStatus: AppointmentPaymentStatus | null;
  onClose: () => void;
  onRefresh: () => void;
  onOpenStripePayment: () => void;
  onCancelSession: (appointmentId: string) => void;
  onJoinCall: () => void;
  coachCurrency?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const MEETING_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', color: 'text-gray-400' },
  { value: 'completed', label: 'Completed', color: 'text-green-400' },
  { value: 'no_show',   label: 'No-Show',   color: 'text-amber-400' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-400' },
] as const;

const STATUS_BADGE: Record<string, string> = {
  scheduled: 'bg-gray-800 text-gray-300',
  completed:  'bg-green-900/50 text-green-300',
  no_show:    'bg-amber-900/50 text-amber-300',
  cancelled:  'bg-gray-700 text-gray-400',
  in_progress:'bg-blue-900/50 text-blue-300',
};

function formatTime(t: string) {
  const [h, m] = t.split(':').map(Number);
  const p = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${p}`;
}

// ── Component ──────────────────────────────────────────────────────────────────

const SessionDetailModal: React.FC<Props> = ({
  session, paymentStatus, onClose, onRefresh, onOpenStripePayment, onCancelSession, onJoinCall,
  coachCurrency,
}) => {
  const apptId = session.appointmentId!;
  const navigate = useNavigate();

  const [currencies, setCurrencies] = useState<Currency[]>([{ code: 'usd', name: 'USD' }]);
  const [symbolMap, setSymbolMap] = useState<SymbolMap>(FALLBACK_SYMBOL_MAP);
  useEffect(() => {
    fetch(`${config.apiBaseUrl}/countries`)
      .then(r => r.ok ? r.json() : [])
      .then((countries: any[]) => {
        setSymbolMap(buildSymbolMap(countries));
        const seen = new Set<string>();
        const unique = countries
          .filter((c: any) => { if (seen.has(c.currency_code)) return false; seen.add(c.currency_code); return true; })
          .map((c: any) => ({ code: c.currency_code.toLowerCase(), name: c.currency_code.toUpperCase() }))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (unique.length > 0) setCurrencies(unique);
      })
      .catch(() => {});
  }, []);

  // ── Meeting status ───────────────────────────────────────────────────────────
  const [meetingStatus, setMeetingStatus] = useState(session.appointmentStatus || 'scheduled');
  const [statusSaving, setStatusSaving] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const meetingLocked = meetingStatus === 'cancelled' || meetingStatus === 'completed';
  const payStatus = paymentStatus?.status ?? 'no_rate';
  const paymentSettled = payStatus === 'succeeded' || payStatus === 'waived' || payStatus === 'manual';
  const everythingLocked = meetingStatus === 'cancelled' || (meetingStatus === 'completed' && paymentSettled);
  const typeRateLocked = everythingLocked || paymentSettled;

  const saveMeetingStatus = async (next: string) => {
    setStatusSaving(true);
    try {
      await updateMeetingStatus(apptId, next as any);
      setMeetingStatus(next);
      setShowStatusMenu(false);
      onRefresh();
    } catch { /* ignore */ }
    finally { setStatusSaving(false); }
  };

  // ── Session type ─────────────────────────────────────────────────────────────
  const [apptType, setApptType] = useState<string>(session.appointmentType || 'billable');
  const [barterNote, setBarterNote] = useState(session.barterNote || '');
  const [typeSaving, setTypeSaving] = useState(false);

  const saveType = async (newType: string, newNote?: string) => {
    setTypeSaving(true);
    try {
      await updateSessionDetails(apptId, {
        appointment_type: newType,
        barter_note: newType === 'barter' ? (newNote ?? barterNote) : '',
      });
      setApptType(newType);
      if (newType !== 'barter') setBarterNote('');
      onRefresh();
    } catch { /* ignore */ }
    finally { setTypeSaving(false); }
  };

  // ── Rate ─────────────────────────────────────────────────────────────────────
  const [rateInput, setRateInput] = useState(
    paymentStatus?.pricing_rate_cents ? (paymentStatus.pricing_rate_cents / 100).toFixed(2) : '',
  );
  const [rateCurrency, setRateCurrency] = useState(
    paymentStatus?.pricing_currency?.toLowerCase() || coachCurrency?.toLowerCase() || 'usd',
  );
  useEffect(() => {
    if (paymentStatus?.pricing_currency) {
      setRateCurrency(paymentStatus.pricing_currency.toLowerCase());
    }
  }, [paymentStatus?.pricing_currency]);
  const [rateSaving, setRateSaving] = useState(false);
  const [rateError, setRateError] = useState('');

  const saveRate = async () => {
    const val = parseFloat(rateInput);
    if (isNaN(val) || val <= 0) { setRateError('Enter a valid amount.'); return; }
    setRateSaving(true);
    setRateError('');
    try {
      await setAppointmentPricing(apptId, val, rateCurrency);
      onRefresh();
    } catch { /* ignore */ }
    finally { setRateSaving(false); }
  };

  // ── Payment actions ──────────────────────────────────────────────────────────
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualMethods, setManualMethods] = useState<{ id: string; name: string }[]>([]);
  const [manualMethod, setManualMethod] = useState('');
  const [manualAmount, setManualAmount] = useState(
    paymentStatus?.pricing_rate_cents ? (paymentStatus.pricing_rate_cents / 100).toFixed(2) : '',
  );
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState('');
  const [waiveSaving, setWaiveSaving] = useState(false);
  const [undoSaving, setUndoSaving] = useState(false);

  useEffect(() => {
    if (!showManualForm) return;
    const token = localStorage.getItem('authToken');
    fetch(`${config.apiBaseUrl}/coaches/me/payment-methods`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then((all: { id: string; name: string; is_active: boolean }[]) => {
        const active = all.filter(m => m.is_active);
        setManualMethods(active);
        if (active.length > 0 && !manualMethod) setManualMethod(active[0].name);
      })
      .catch(() => {});
  }, [showManualForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveManualPayment = async () => {
    const dollars = parseFloat(manualAmount);
    if (!manualAmount || isNaN(dollars) || dollars <= 0) { setManualError('Enter a valid amount.'); return; }
    if (!manualMethod) { setManualError('Select a payment method.'); return; }
    setManualSaving(true);
    setManualError('');
    try {
      await recordManualPayment(apptId, {
        amount_cents: Math.round(dollars * 100),
        payment_method: manualMethod as any,
      });
      setShowManualForm(false);
      onRefresh();
      onClose();
    } catch (e: any) {
      setManualError(e.message || 'Failed to record payment.');
    } finally { setManualSaving(false); }
  };

  const waivePayment = async () => {
    setWaiveSaving(true);
    try {
      await updatePricingStatus(apptId, 'waived');
      onRefresh();
      onClose();
    } catch { /* ignore */ }
    finally { setWaiveSaving(false); }
  };

  const undoManualPayment = async () => {
    setUndoSaving(true);
    try {
      await resetManualPayment(apptId);
      onRefresh();
      onClose();
    } catch { /* ignore */ }
    finally { setUndoSaving(false); }
  };

  // ── Package enrollment ────────────────────────────────────────────────────────
  const [pkgEnrollmentId, setPkgEnrollmentId] = useState<string | null>(session.packageEnrollmentId ?? null);
  const [pkgName, setPkgName] = useState<string | null>(session.packageName ?? null);
  const [pkgEnrollments, setPkgEnrollments] = useState<{ id: string; package_name: string }[]>([]);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [pkgError, setPkgError] = useState('');

  useEffect(() => {
    if (!session.coachClientId) return;
    const token = localStorage.getItem('authToken');
    fetch(`${config.apiBaseUrl}/packages/enrollments?status=active&coach_client_id=${session.coachClientId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => setPkgEnrollments(rows.map(r => ({ id: r.id, package_name: r.package_name }))))
      .catch(() => {});
  }, [session.coachClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePackageEnrollment = async (newId: string | null) => {
    if (!apptId) return;
    const prevId = pkgEnrollmentId;
    const prevName = pkgName;
    setPkgError('');
    setPkgEnrollmentId(newId);
    setPkgName(newId ? (pkgEnrollments.find(e => e.id === newId)?.package_name ?? null) : null);
    setPkgSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${config.apiBaseUrl}/coaches/sessions/${apptId}/package-enrollment`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ enrollment_id: newId }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to update');
      }
      onRefresh();
    } catch (e: any) {
      setPkgEnrollmentId(prevId);
      setPkgName(prevName);
      setPkgError(e.message || 'Failed to save package link');
    } finally { setPkgSaving(false); }
  };

  // ── Invoice ──────────────────────────────────────────────────────────────────
  const [invoice, setInvoice] = useState<Invoice | null | undefined>(undefined);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');

  useEffect(() => {
    if (!apptId) return;
    getInvoiceForSession(apptId).then(setInvoice).catch(() => setInvoice(null));
  }, [apptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateInvoice = async () => {
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const inv = await createInvoice({ appointment_id: apptId });
      setInvoice(inv);
    } catch (e: any) {
      setInvoiceError(e.message || 'Failed to create invoice');
    } finally { setInvoiceLoading(false); }
  };

  const handleSendInvoice = async () => {
    if (!invoice) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const inv = await sendInvoice(invoice.id);
      setInvoice(inv);
    } catch (e: any) {
      setInvoiceError(e.message || 'Failed to send invoice');
    } finally { setInvoiceLoading(false); }
  };

  const handleResendInvoice = async () => {
    if (!invoice) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const inv = await resendInvoice(invoice.id);
      setInvoice(inv);
    } catch (e: any) {
      setInvoiceError(e.message || 'Failed to resend invoice');
    } finally { setInvoiceLoading(false); }
  };

  const handleVoidInvoice = async () => {
    if (!invoice) return;
    if (!window.confirm('Void this invoice? This cannot be undone.')) return;
    setInvoiceLoading(true);
    setInvoiceError('');
    try {
      const inv = await voidInvoice(invoice.id);
      setInvoice(inv);
    } catch (e: any) {
      setInvoiceError(e.message || 'Failed to void invoice');
    } finally { setInvoiceLoading(false); }
  };

  // ── Date / time display ──────────────────────────────────────────────────────
  const dateLabel = new Date(session.date + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const currentStatusCfg = MEETING_STATUS_OPTIONS.find(o => o.value === meetingStatus);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-5 py-4 flex items-center justify-between border-b border-dark-border ${
          meetingStatus === 'completed' ? 'bg-green-900/30' :
          meetingStatus === 'cancelled' ? 'bg-gray-800/50' :
          'bg-primary-900/30'
        }`}>
          <div>
            <p className="text-white font-semibold">{session.clientName}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {dateLabel} · {formatTime(session.time)} · {session.duration} min
            </p>
          </div>
          <div className="flex items-center gap-2">
            {session.appointmentId && (
              <button
                onClick={() => { onClose(); navigate(`/dashboard/coach/session/${session.appointmentId}`); }}
                className="text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors px-2 py-1 rounded hover:bg-primary-900/40"
              >
                Open session
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">

          {/* ── Meeting Status ─────────────────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Meeting Status
            </label>
            {meetingLocked ? (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[meetingStatus] || STATUS_BADGE.scheduled}`}>
                {currentStatusCfg?.label ?? meetingStatus}
              </span>
            ) : (
              <div className="relative">
                <button
                  disabled={statusSaving}
                  onClick={() => setShowStatusMenu(p => !p)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    STATUS_BADGE[meetingStatus] || 'text-gray-400'
                  } border-dark-border hover:border-gray-500 bg-dark-canvas`}
                >
                  <span className="capitalize">{currentStatusCfg?.label ?? meetingStatus}</span>
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showStatusMenu && (
                  <div className="absolute left-0 top-full mt-1 bg-dark-card border border-dark-border rounded-xl shadow-xl z-10 py-1 min-w-[160px]">
                    {MEETING_STATUS_OPTIONS.filter(o => o.value !== meetingStatus).map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => saveMeetingStatus(opt.value)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-dark-hover transition-colors ${opt.color}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Session Type ───────────────────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Session Type
            </label>
            {typeRateLocked ? (
              <span className="text-sm text-gray-300 capitalize">
                {getAppointmentTypeLabel(apptType)}
                {apptType === 'barter' && barterNote && <span className="text-gray-400 ml-2">— {barterNote}</span>}
              </span>
            ) : (
              <div className="space-y-2">
                <div className="flex rounded-lg border border-dark-border overflow-hidden text-sm">
                  {APPOINTMENT_TYPES.map(v => (
                    <button
                      key={v}
                      disabled={typeSaving}
                      onClick={() => { setApptType(v); if (v !== 'barter') saveType(v); }}
                      className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                        apptType === v
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-dark-hover'
                      }`}
                    >
                      {getAppointmentTypeLabel(v)}
                    </button>
                  ))}
                </div>
                {apptType === 'barter' && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={barterNote}
                      onChange={e => setBarterNote(e.target.value)}
                      placeholder="Describe the barter arrangement…"
                      className="flex-1 px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500"
                    />
                    <button
                      disabled={typeSaving}
                      onClick={() => saveType('barter', barterNote)}
                      className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Package ───────────────────────────────────────────────────── */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
              Package
            </label>
            {meetingLocked ? (
              <span className="text-sm text-gray-300">
                {pkgName ?? <span className="text-gray-500 italic">None</span>}
              </span>
            ) : (
              <>
                <select
                  disabled={pkgSaving}
                  value={pkgEnrollmentId ?? ''}
                  onChange={e => savePackageEnrollment(e.target.value || null)}
                  className="w-full px-3 py-1.5 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 focus:outline-none focus:border-primary-500 disabled:opacity-50"
                >
                  <option value="">— Not linked to a package —</option>
                  {pkgEnrollments.map(e => (
                    <option key={e.id} value={e.id}>{e.package_name}</option>
                  ))}
                </select>
                {pkgSaving && <p className="text-xs text-gray-500 mt-1">Saving…</p>}
                {pkgError && <p className="text-xs text-red-400 mt-1">{pkgError}</p>}
              </>
            )}
          </div>

          {/* ── Rate ──────────────────────────────────────────────────────── */}
          {(apptType === 'billable') && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Session Rate
              </label>
              {typeRateLocked ? (
                <span className="text-sm text-gray-300">
                  {paymentStatus?.pricing_rate_cents
                    ? `${currencySymbol(paymentStatus.pricing_currency, symbolMap)}${(paymentStatus.pricing_rate_cents / 100).toFixed(2)}`
                    : '—'}
                </span>
              ) : (
                <div className="flex gap-2 items-start">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rateInput}
                    onChange={e => { setRateInput(e.target.value); setRateError(''); }}
                    onKeyDown={e => e.key === 'Enter' && saveRate()}
                    placeholder="0.00"
                    className="px-3 py-1.5 w-28 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500"
                  />
                  <select
                    value={rateCurrency}
                    onChange={e => setRateCurrency(e.target.value)}
                    className="border border-dark-border rounded-lg px-2 py-1.5 text-sm bg-dark-canvas text-gray-200 focus:outline-none"
                  >
                    {currencies.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    disabled={rateSaving}
                    onClick={saveRate}
                    className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {rateSaving ? 'Saving…' : 'Set Rate'}
                  </button>
                  {rateError && <p className="text-xs text-red-400 mt-1">{rateError}</p>}
                </div>
              )}
            </div>
          )}

          {/* ── Payment ───────────────────────────────────────────────────── */}
          {apptType === 'billable' && <div>
            <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
              Payment
            </label>

            {/* Current payment status badge */}
            <div className="flex items-center gap-2 mb-3">
              {payStatus === 'no_rate' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 border border-dark-border">No Rate Set</span>
              )}
              {payStatus === 'unpaid' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-800/50">Unpaid</span>
              )}
              {payStatus === 'succeeded' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 border border-green-800/50">Paid via Stripe</span>
              )}
              {payStatus === 'manual' && (
                <>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-800/50">
                    Paid Manually{paymentStatus?.payment?.payment_method ? ` · ${paymentStatus.payment.payment_method}` : ''}
                  </span>
                  {!everythingLocked && (
                    <button
                      disabled={undoSaving}
                      onClick={undoManualPayment}
                      title="Undo manual payment"
                      className="text-xs text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                    >
                      {undoSaving ? 'Undoing…' : '✕ Undo'}
                    </button>
                  )}
                </>
              )}
              {payStatus === 'waived' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 border border-dark-border">Waived</span>
              )}
              {(payStatus === 'pending' || payStatus === 'processing') && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300 border border-yellow-800/50">Pending</span>
              )}
              {payStatus === 'refunded' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 border border-dark-border">Refunded</span>
              )}
            </div>

            {/* Payment actions — shown only when actionable */}
            {!everythingLocked && !paymentSettled && (payStatus === 'unpaid' || payStatus === 'no_rate') && (
              <div className="space-y-2">
                {/* Manual payment form */}
                {showManualForm ? (
                  <div className="bg-dark-canvas border border-dark-border rounded-xl p-3 space-y-3">
                    <p className="text-xs font-medium text-gray-300">Record Manual Payment</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currencySymbol(paymentStatus?.pricing_currency, symbolMap)}</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={manualAmount}
                          onChange={e => { setManualAmount(e.target.value); setManualError(''); }}
                          placeholder="0.00"
                          className="pl-7 pr-3 py-1.5 w-full text-sm bg-dark-card border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>
                    {manualMethods.length === 0 ? (
                      <p className="text-xs text-gray-400">No payment methods configured. Add them in Settings → Payments.</p>
                    ) : (
                      <select
                        value={manualMethod}
                        onChange={e => setManualMethod(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm bg-dark-card border border-dark-border rounded-lg text-gray-200 focus:outline-none focus:border-primary-500"
                      >
                        {manualMethods.map(m => (
                          <option key={m.id} value={m.name}>{m.name}</option>
                        ))}
                      </select>
                    )}
                    {manualError && <p className="text-xs text-red-400">{manualError}</p>}
                    <div className="flex gap-2">
                      <button
                        disabled={manualSaving || manualMethods.length === 0}
                        onClick={saveManualPayment}
                        className="flex-1 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 transition-colors"
                      >
                        {manualSaving ? 'Saving…' : 'Confirm Payment'}
                      </button>
                      <button
                        onClick={() => setShowManualForm(false)}
                        className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => { setShowManualForm(true); }}
                      className="px-3 py-1.5 text-xs bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-800/50 rounded-lg font-medium transition-colors"
                    >
                      Mark Paid Manually
                    </button>
                    {apptType === 'billable' && payStatus !== 'no_rate' && (
                      <button
                        onClick={() => { onClose(); onOpenStripePayment(); }}
                        className="px-3 py-1.5 text-xs bg-primary-900/40 hover:bg-primary-800/60 text-primary-300 border border-primary-800/50 rounded-lg font-medium transition-colors"
                      >
                        Charge via Stripe
                      </button>
                    )}
                    <button
                      disabled={waiveSaving || payStatus === 'no_rate'}
                      onClick={waivePayment}
                      title={payStatus === 'no_rate' ? 'Set a rate before waiving' : undefined}
                      className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      {waiveSaving ? 'Waiving…' : 'Waive Payment'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {everythingLocked && (
              <p className="text-xs text-gray-500 italic">
                {meetingStatus === 'cancelled' ? 'No changes allowed for cancelled sessions.' : 'Session is completed and paid — no further changes allowed.'}
              </p>
            )}

            {/* ── Invoice ─────────────────────────────────────────────────── */}
            {meetingStatus !== 'cancelled' && (
              <div className="mt-3 border-t border-dark-border pt-3">
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Invoice</label>
                {invoice === undefined ? (
                  <p className="text-xs text-gray-500">Loading…</p>
                ) : invoice === null || invoice.status === 'void' ? (
                  <div>
                    {!everythingLocked && payStatus !== 'no_rate' && (invoice === null || invoice.status === 'void') && (
                      <button
                        disabled={invoiceLoading}
                        onClick={handleCreateInvoice}
                        className="px-3 py-1.5 text-xs bg-indigo-900/40 hover:bg-indigo-800/60 text-indigo-300 border border-indigo-800/50 rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        {invoiceLoading ? 'Creating…' : '+ Create Invoice'}
                      </button>
                    )}
                    {invoice?.status === 'void' && (
                      <span className="text-xs text-gray-500 ml-2">Invoice voided</span>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Invoice number */}
                      <span className="text-xs text-gray-400">{invoice.invoice_number}</span>
                      {/* Status badge */}
                      {invoice.status === 'draft' && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-300 border border-dark-border">Draft</span>}
                      {invoice.status === 'sent' && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-800/50">Sent</span>}
                      {invoice.status === 'viewed' && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-900/40 text-purple-300 border border-purple-800/50">Viewed</span>}
                      {invoice.status === 'paid' && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 border border-green-800/50">Paid</span>}
                      {invoice.status === 'overdue' && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-800/50">Overdue</span>}
                      {/* Payment URL copy */}
                      {invoice.status !== 'paid' && (
                        <button
                          onClick={() => { navigator.clipboard.writeText(invoice.payment_url); }}
                          className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                          title="Copy payment link"
                        >
                          Copy link
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {invoice.status === 'draft' && (
                        <button
                          disabled={invoiceLoading}
                          onClick={handleSendInvoice}
                          className="px-3 py-1.5 text-xs bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 border border-blue-800/50 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {invoiceLoading ? 'Sending…' : 'Send Invoice'}
                        </button>
                      )}
                      {(invoice.status === 'sent' || invoice.status === 'viewed' || invoice.status === 'overdue') && (
                        <button
                          disabled={invoiceLoading}
                          onClick={handleResendInvoice}
                          className="px-3 py-1.5 text-xs bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 border border-blue-800/50 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {invoiceLoading ? 'Resending…' : 'Resend Invoice'}
                        </button>
                      )}
                      {invoice.status !== 'paid' && (
                        <button
                          disabled={invoiceLoading}
                          onClick={handleVoidInvoice}
                          className="px-3 py-1.5 text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-red-400 border border-dark-border rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                          {invoiceLoading ? 'Voiding…' : 'Void'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {invoiceError && <p className="text-xs text-red-400 mt-1">{invoiceError}</p>}
              </div>
            )}
          </div>}
        </div>

        {/* Footer actions */}
        <div className="border-t border-dark-border px-5 py-3 flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {session.googleMeetLink && (
              <button
                onClick={() => { onClose(); onJoinCall(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                <VideoCameraIcon className="w-3.5 h-3.5" />
                Join Call
              </button>
            )}
            {!everythingLocked && meetingStatus !== 'completed' && payStatus !== 'succeeded' && payStatus !== 'manual' && session.appointmentId && (
              <button
                onClick={() => { onClose(); onCancelSession(session.appointmentId!); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-hover hover:bg-gray-700 text-red-400 border border-dark-border rounded-lg font-medium transition-colors"
              >
                <XCircleIcon className="w-3.5 h-3.5" />
                Cancel Session
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;
